























import Vue, { PropType } from 'vue';

import 'pdfjs-dist/web/pdf_viewer.css';
import {
  PDFDocumentLoadingTask,
  PDFPageProxy,
  PageViewport,
  RenderTask
} from 'pdfjs-dist';
import {
  GetViewportParameters,
  PDFDocumentProxy,
  RenderParameters
} from 'pdfjs-dist/types/src/display/api';

interface InternalProps {
  page?: PDFPageProxy;
  document?: PDFDocumentProxy;
  viewport?: PageViewport;
}

interface Options extends InstanceType<typeof Vue> {
  $refs: {
    container: HTMLDivElement;
    loadingLayer: HTMLDivElement;
  };
}

export default Vue.extend<Options>({ functional: false }).extend({
  name: 'PDFViewer',

  props: {
    pdf: {
      type: Object as PropType<PDFDocumentLoadingTask>
    },
    page: {
      type: Number,
      default: 1
    },
    scale: {
      type: Number,
      default: 1
    },
    rotation: {
      type: Number
    },
    fitParent: {
      type: Boolean
    },
    width: {
      type: Number
    },
    height: {
      type: Number
    },
    intent: {
      type: String,
      default: 'display'
    },
    pages: {
      type: Number,
      default: 1
    },
    showAllPages: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      loading: true,
      renderTask: null as null | RenderTask,
      internalProps: {} as InternalProps
    };
  },

  watch: {
    pdf: {
      handler(pdfValue) {
        // For any changes on pdf, reinitialize document file
        if (pdfValue) this.initDoc(pdfValue);
      }
    },

    scale() {
      this.renderPage(this.page);
    },
    width() {
      this.renderPage(this.page);
    },
    height() {
      this.renderPage(this.page);
    },
    rotation() {
      this.renderPage(this.page);
    },
    page() {
      this.renderPage(this.page);
    },
    hideForms() {
      this.renderPage(this.page);
    },
    intent() {
      this.renderPage(this.page);
    }
  },

  mounted() {
    if (this.pdf) {
      this.initDoc(this.pdf);
    }
  },

  methods: {
    initDoc(proxy: PDFDocumentLoadingTask) {
      proxy.promise.then(async document => {
        this.internalProps.document = document;

        if (this.showAllPages) {
          this.renderAllPages();
        } else {
          this.renderPage(this.page);
        }
      });
    },

    renderAllPages() {
      try {
        for (let pageNum = 1; pageNum <= this.pages; pageNum++) {
          this.internalProps.document?.getPage(pageNum).then(page => {
            const defaultViewport = page.getViewport();
            const viewportParams: GetViewportParameters = {
              scale: this.getScale(page),
              rotation: this.getRotation(
                (this.rotation || 0) + defaultViewport.rotation
              )
            };
            const viewport = page.getViewport(viewportParams);

            const oldCanvas = (this.$refs['canvas_' + pageNum] as any)[0];
            const canvas = this.setupCanvas(viewport, pageNum);

            const outputScale = window.devicePixelRatio || 1;
            const transform =
              outputScale !== 1
                ? [outputScale, 0, 0, outputScale, 0, 0]
                : undefined;

            // Render PDF page into canvas context
            const renderContext: RenderParameters = {
              canvasContext: canvas.getContext('2d')!,
              viewport,
              transform,
              intent: this.intent
            };

            if (oldCanvas) this.$refs.container.replaceChild(canvas, oldCanvas);

            this.internalProps.page = page;
            this.internalProps.viewport = viewport;
            this.renderTask = page.render(renderContext);
            this.renderTask.promise
              .then(() => {
                this.loading = false;
                this.$emit('loaded', this.internalProps.viewport!);
              })
              .catch(() => {
                // render task cancelled
              });
          });
        }
      } catch (e) {
        this.$emit('loading-failed', e);
        this.loading = false;
      }
    },

    renderPage(pageNum: number) {
      try {
        this.internalProps.document?.getPage(pageNum).then(page => {
          this.cancelRender();

          const defaultViewport = page.getViewport();
          const viewportParams: GetViewportParameters = {
            scale: this.getScale(page),
            rotation: this.getRotation(
              (this.rotation || 0) + defaultViewport.rotation
            )
          };
          const viewport = page.getViewport(viewportParams);

          const oldCanvas = this.getCurrentCanvas();
          const canvas = this.setupCanvas(viewport);

          const outputScale = window.devicePixelRatio || 1;
          const transform =
            outputScale !== 1
              ? [outputScale, 0, 0, outputScale, 0, 0]
              : undefined;

          // Render PDF page into canvas context
          const renderContext: RenderParameters = {
            canvasContext: canvas.getContext('2d')!,
            viewport,
            transform,
            intent: this.intent
          };

          if (canvas?.getAttribute('role') !== 'main') {
            if (oldCanvas) this.$refs.container.replaceChild(canvas, oldCanvas);
          } else {
            canvas.removeAttribute('role');
          }

          this.internalProps.page = page;
          this.internalProps.viewport = viewport;
          this.renderTask = page.render(renderContext);
          this.renderTask.promise
            .then(() => {
              this.loading = false;
              this.$emit('loaded', this.internalProps.viewport!);
            })
            .catch(() => {
              // render task cancelled
            });
        });
      } catch (e) {
        this.$emit('loading-failed', e);
        this.loading = false;
      }
    },

    cancelRender() {
      if (this.renderTask) this.renderTask.cancel();
    },

    // viewport functions
    getRotation(rotation: number): number {
      if (!(typeof rotation === 'number' && rotation % 90 === 0)) return 0;
      const factor = rotation / 90;
      if (factor > 4) return this.getRotation(rotation - 360);
      else if (factor < 0) return this.getRotation(rotation + 360);
      return rotation;
    },

    getScale(page: PDFPageProxy): number {
      let scale = this.scale;
      if (this.fitParent) {
        const parentWidth: number = (this.$refs.container!
          .parentNode! as HTMLElement).clientWidth;
        const scale1Width = page.getViewport({ scale: 1 }).width;
        scale = parentWidth / scale1Width;
      } else if (this.width) {
        const scale1Width = page.getViewport({ scale: 1 }).width;
        scale = this.width / scale1Width;
      } else if (this.height) {
        const scale1Height = page.getViewport({ scale: 1 }).height;
        scale = this.height / scale1Height;
      }
      return scale;
    },

    // Canvas
    getCurrentCanvas(): HTMLCanvasElement | null {
      let oldCanvas = null;
      this.$refs.container.childNodes.forEach(el => {
        if ((el as HTMLElement).tagName === 'CANVAS') oldCanvas = el;
      });
      return oldCanvas;
    },

    setupCanvas(viewport: PageViewport, pageNum?: number): HTMLCanvasElement {
      let canvas;
      const currentCanvas = this.showAllPages
        ? (this.$refs['canvas_' + pageNum] as any)[0]
        : this.getCurrentCanvas();

      if (currentCanvas && currentCanvas?.getAttribute('role') === 'main') {
        canvas = currentCanvas;
      } else {
        canvas = document.createElement('canvas');
        canvas.style.display = 'block';
        canvas.setAttribute('dir', 'ltr');
      }

      const outputScale = window.devicePixelRatio || 1;
      canvas.width = Math.floor(viewport.width * outputScale);
      canvas.height = Math.floor(viewport.height * outputScale);

      canvas.style.width = `${Math.floor(viewport.width)}px`;
      canvas.style.height = `${Math.floor(viewport.height)}px`;

      // --scale-factor property
      this.$refs.container?.style.setProperty(
        '--scale-factor',
        `${viewport.scale}`
      );
      // Also setting dimension properties for load layer
      this.$refs.loadingLayer.style.width = `${Math.floor(viewport.width)}px`;
      this.$refs.loadingLayer.style.height = `${Math.floor(viewport.height)}px`;
      this.$refs.loadingLayer.style.top = '0';
      this.$refs.loadingLayer.style.left = '0';
      this.loading = true;
      return canvas;
    },

    // exposed methods for reference usage
    reload() {
      if (this.showAllPages) {
        this.renderAllPages();
      } else {
        this.renderPage(this.page);
      }
    }
  }
});
